import { alpha, useTheme } from "@mui/material";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { Layout, PlotData } from "plotly.js";
import { useMemo } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { defaultLayout, setSelectedDateFromPlotClick } from "../plotting";
import { SavingsTimeseriesPoint, UnitType } from "src/store/api/generatedApi";

const MARKER_SIZE = 8;

interface CumulativeSavingsPlotProps {
  data: SavingsTimeseriesPoint[];
  includeAlloy: boolean;
  setSelectedDate: (date: Date) => void;
}

export const CumulativeSavingsPlot = ({
  data,
  includeAlloy,
  setSelectedDate,
}: CumulativeSavingsPlotProps) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(false);
  const theme = useTheme();

  const lineColor = theme.palette.success.main;
  const fillColor = alpha(lineColor, 0.2);

  const plotData = useMemo(
    () => buildFigData(data, includeAlloy, t, lineColor, fillColor),
    [data, includeAlloy, t, lineColor, fillColor]
  );
  const layout = useMemo(() => buildLayout(t, units), [t, units]);

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={plotData}
        layout={layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
    </div>
  );
};

const buildFigData = (
  data: SavingsTimeseriesPoint[],
  includeAlloy: boolean,
  t: (key: string) => string,
  lineColor: string,
  fillColor: string
): Partial<PlotData>[] => {
  const allData = data.filter((d) => d.standard === "all");
  const dates = allData.map((d) => d.period_start);
  const values = allData.map(
    (d) =>
      d.cumulative_savings + (includeAlloy ? d.alloy_cumulative_savings : 0)
  );

  return [
    {
      x: dates,
      y: values,
      type: "scatter",
      mode: "lines+markers",
      name: t("cumulativeSavings"),
      line: { color: lineColor, width: 3 },
      marker: { color: lineColor, size: MARKER_SIZE },
      fill: "tozeroy",
      fillcolor: fillColor,
    },
  ];
};

const buildLayout = (
  t: (key: string) => string,
  units: (unitType: UnitType) => string
): Partial<Layout> => ({
  ...defaultLayout,
  height: 500,
  yaxis: {
    title: { text: `${t("cumulativeSavings")}${units("cost")}` },
  },
  xaxis: {
    title: { text: t("date") },
  },
});
